import {FreelancerSignupWorkflowFreelancerSignupWorkflowStateEnum, SignupCompletionCompletionEnum, SignupCompletionField} from '@api';
import {FreelancerSignupContext} from '~/features/freelancer-signup/_common/types/FreelancerSignupContext';
import type {FreelancerSignupStepConfiguration} from '~/features/freelancer-signup/navigation/types/FreelancerSignupStep';
import {
    FreelancerSignupStepGroup,
    FreelancerSignupStepName,
    FreelancerSignupStepRoute,
} from '~/features/freelancer-signup/navigation/types/FreelancerSignupStep';
import type {Workflow} from '~/features/freelancer-signup/workflow/types/Workflow';
import type {FeatureContext} from '~/store/featureTypes';

type Condition = {
    state?: FreelancerSignupWorkflowFreelancerSignupWorkflowStateEnum;
    completed?: SignupCompletionField[];
    toComplete?: SignupCompletionField[];
    skipped?: SignupCompletionField[];
    context?: FreelancerSignupContext[];
};

type StepConfiguration = Omit<FreelancerSignupStepConfiguration, 'subStep'>;

export function buildAllSteps(
    funnelContext: FreelancerSignupContext,
    workflow: Workflow | null,
    features: Partial<FeatureContext> = {},
): FreelancerSignupStepConfiguration[] {
    const allStepDefinition: StepConfiguration[] = [_defineFirstStepFromContext(funnelContext, workflow)];

    const addProfilePictureStep = workflow != null && !workflow.ignoredSteps.includes(FreelancerSignupStepName.YOUR_PROFILE_PICTURE);
    allStepDefinition.push({
        name: FreelancerSignupStepName.FREELANCING_EXPERIENCE,
        routeTo: FreelancerSignupStepRoute.FREELANCING_EXPERIENCE,
        group: FreelancerSignupStepGroup.INTRODUCE_YOURSELF,
        isAccessible: _workflowSatisfies(workflow, {
            toComplete: [SignupCompletionField.FreelancingExperience],
        }),
        isCompleted: _workflowSatisfies(workflow, {
            completed: [SignupCompletionField.FreelancingExperience],
        }),
        previousEnabled: false,
        hideShapes: false,
    });

    if (_contextSatisfies(funnelContext, [FreelancerSignupContext.STRATEGY])) {
        allStepDefinition.push({
            name: FreelancerSignupStepName.DESCRIBE_YOURSELF,
            routeTo: FreelancerSignupStepRoute.DESCRIBE_YOURSELF,
            group: FreelancerSignupStepGroup.INTRODUCE_YOURSELF,
            isAccessible: _workflowSatisfies(workflow, {
                context: [FreelancerSignupContext.STRATEGY],
                completed: [SignupCompletionField.FreelancingExperience],
            }),
            isCompleted: _workflowSatisfies(workflow, {
                completed: [SignupCompletionField.StrategyBackground],
            }),
            previousEnabled: false,
            hideShapes: false,
        });
    }
    allStepDefinition.push({
        name: FreelancerSignupStepName.CREATE_YOUR_ACCOUNT,
        routeTo: FreelancerSignupStepRoute.CREATE_YOUR_ACCOUNT,
        group: FreelancerSignupStepGroup.INTRODUCE_YOURSELF,
        isAccessible: _workflowSatisfiesOneOf(workflow, [
            {
                context: [FreelancerSignupContext.REGULAR, FreelancerSignupContext.OPEN],
                completed: [SignupCompletionField.FreelancingExperience],
            },
            {
                context: [FreelancerSignupContext.STRATEGY],
                completed: [SignupCompletionField.StrategyBackground],
            },
        ]),
        isCompleted: _workflowSatisfiesOneOf(workflow, [
            {
                state: FreelancerSignupWorkflowFreelancerSignupWorkflowStateEnum.EmailValidationPending,
                completed: [SignupCompletionField.Name, SignupCompletionField.TermsAndConditions, SignupCompletionField.MarketingEmailsOptIn],
            },
            {
                state: FreelancerSignupWorkflowFreelancerSignupWorkflowStateEnum.EmailValidated,
                completed: [SignupCompletionField.Name, SignupCompletionField.TermsAndConditions, SignupCompletionField.MarketingEmailsOptIn],
            },
            {
                state: FreelancerSignupWorkflowFreelancerSignupWorkflowStateEnum.EmailValidated,
                completed: [SignupCompletionField.SocialLogin],
            },
        ]),
        previousEnabled: false,
        hideShapes: false,
    });
    if (
        // needed only in case of social login: email is validated but account details are not confirmed
        _workflowSatisfies(workflow, {
            state: FreelancerSignupWorkflowFreelancerSignupWorkflowStateEnum.EmailValidated,
            completed: [SignupCompletionField.SocialLogin],
        })
    ) {
        allStepDefinition.push({
            name: FreelancerSignupStepName.COMPLETE_YOUR_ACCOUNT,
            routeTo: FreelancerSignupStepRoute.COMPLETE_YOUR_ACCOUNT,
            group: FreelancerSignupStepGroup.INTRODUCE_YOURSELF,
            isAccessible: true,
            isCompleted: _workflowSatisfies(workflow, {
                completed: [SignupCompletionField.Name, SignupCompletionField.TermsAndConditions, SignupCompletionField.MarketingEmailsOptIn],
            }),
            previousEnabled: false,
            hideShapes: false,
        });
    } else {
        allStepDefinition.push({
            name: FreelancerSignupStepName.VERIFY_YOUR_EMAIL,
            routeTo: FreelancerSignupStepRoute.VERIFY_YOUR_EMAIL,
            group: FreelancerSignupStepGroup.INTRODUCE_YOURSELF,
            isAccessible: _workflowSatisfies(workflow, {
                state: FreelancerSignupWorkflowFreelancerSignupWorkflowStateEnum.EmailValidationPending,
                completed: [SignupCompletionField.Name, SignupCompletionField.TermsAndConditions, SignupCompletionField.MarketingEmailsOptIn],
            }),
            isCompleted: _workflowSatisfies(workflow, {
                state: FreelancerSignupWorkflowFreelancerSignupWorkflowStateEnum.EmailValidated,
            }),
            previousEnabled: false,
            hideShapes: false,
        });
    }
    allStepDefinition.push(
        {
            name: FreelancerSignupStepName.SHOWCASE_YOUR_TALENTS,
            routeTo: FreelancerSignupStepRoute.SHOWCASE_YOUR_TALENTS,
            group: FreelancerSignupStepGroup.SHOWCASE_YOUR_TALENT,
            isAccessible: _workflowSatisfies(workflow, {
                state: FreelancerSignupWorkflowFreelancerSignupWorkflowStateEnum.EmailValidated,
                completed: [SignupCompletionField.Name, SignupCompletionField.TermsAndConditions, SignupCompletionField.MarketingEmailsOptIn],
            }),
            isCompleted: _workflowSatisfiesOneOf(workflow, [
                {
                    state: FreelancerSignupWorkflowFreelancerSignupWorkflowStateEnum.EmailValidated,
                    completed: [SignupCompletionField.ImportResume],
                },
                {
                    state: FreelancerSignupWorkflowFreelancerSignupWorkflowStateEnum.EmailValidated,
                    skipped: [SignupCompletionField.ImportResume],
                },
                {
                    context: [FreelancerSignupContext.OPEN],
                    state: FreelancerSignupWorkflowFreelancerSignupWorkflowStateEnum.EmailValidated,
                    skipped: [SignupCompletionField.WorkExperiences],
                },
            ]),
            previousEnabled: false,
            hideShapes: true,
        },
        {
            name: FreelancerSignupStepName.WORK_EXPERIENCE,
            routeTo: FreelancerSignupStepRoute.WORK_EXPERIENCE,
            group: FreelancerSignupStepGroup.SHOWCASE_YOUR_TALENT,
            isAccessible: _workflowSatisfiesOneOf(workflow, [
                {
                    completed: [SignupCompletionField.ImportResume],
                },
                {
                    skipped: [SignupCompletionField.ImportResume],
                },
                {
                    skipped: [SignupCompletionField.WorkExperiences],
                },
            ]),
            isCompleted: _workflowSatisfiesOneOf(workflow, [
                {
                    completed: [SignupCompletionField.WorkExperiences],
                },
                {
                    context: [FreelancerSignupContext.OPEN],
                    skipped: [SignupCompletionField.WorkExperiences],
                },
            ]),
            previousEnabled: true,
            hideShapes: true,
        },
    );

    if (_contextSatisfies(funnelContext, [FreelancerSignupContext.STRATEGY])) {
        allStepDefinition.push({
            name: FreelancerSignupStepName.EDUCATION,
            routeTo: FreelancerSignupStepRoute.EDUCATION,
            group: FreelancerSignupStepGroup.SHOWCASE_YOUR_TALENT,
            isAccessible: _workflowSatisfies(workflow, {
                context: [FreelancerSignupContext.STRATEGY],
                completed: [SignupCompletionField.WorkExperiences],
            }),
            isCompleted: _workflowSatisfies(workflow, {
                completed: [SignupCompletionField.Educations],
            }),
            previousEnabled: true,
            hideShapes: true,
        });
    }

    allStepDefinition.push({
        name: FreelancerSignupStepName.JOB_TITLE_AND_CATEGORY,
        routeTo: FreelancerSignupStepRoute.JOB_TITLE_AND_CATEGORY,
        group: FreelancerSignupStepGroup.SHOWCASE_YOUR_TALENT,
        isAccessible: _workflowSatisfiesOneOf(workflow, [
            {
                context: [FreelancerSignupContext.REGULAR, FreelancerSignupContext.OPEN],
                completed: [SignupCompletionField.WorkExperiences],
            },
            {
                context: [FreelancerSignupContext.OPEN],
                skipped: [SignupCompletionField.WorkExperiences],
            },
            {
                context: [FreelancerSignupContext.STRATEGY],
                completed: [SignupCompletionField.Educations],
            },
        ]),
        isCompleted: _workflowSatisfies(workflow, {
            completed: [SignupCompletionField.Headline, SignupCompletionField.Categories],
        }),
        previousEnabled: true,
        hideShapes: true,
    });

    if (_contextSatisfies(funnelContext, [FreelancerSignupContext.STRATEGY])) {
        allStepDefinition.push(
            {
                name: FreelancerSignupStepName.YOUR_FUNCTIONAL_AND_GLOBAL_SKILLS,
                routeTo: FreelancerSignupStepRoute.YOUR_FUNCTIONAL_AND_GLOBAL_SKILLS,
                group: FreelancerSignupStepGroup.SHOWCASE_YOUR_TALENT,
                isAccessible: _workflowSatisfies(workflow, {
                    context: [FreelancerSignupContext.STRATEGY],
                    completed: [SignupCompletionField.Headline, SignupCompletionField.Categories],
                }),
                isCompleted: _workflowSatisfies(workflow, {
                    context: [FreelancerSignupContext.STRATEGY],
                    completed: [SignupCompletionField.StrategySkills],
                }),
                previousEnabled: true,
                hideShapes: true,
            },
            {
                name: FreelancerSignupStepName.YOUR_INDUSTRY_EXPERTISE,
                routeTo: FreelancerSignupStepRoute.YOUR_INDUSTRY_EXPERTISE,
                group: FreelancerSignupStepGroup.SHOWCASE_YOUR_TALENT,
                isAccessible: _workflowSatisfies(workflow, {
                    context: [FreelancerSignupContext.STRATEGY],
                    completed: [SignupCompletionField.StrategySkills],
                }),
                isCompleted: _workflowSatisfies(workflow, {
                    context: [FreelancerSignupContext.STRATEGY],
                    completed: [SignupCompletionField.IndustryExpertises],
                }),
                previousEnabled: true,
                hideShapes: true,
            },
        );
    } else {
        allStepDefinition.push({
            name: FreelancerSignupStepName.YOUR_TOP_SKILLS,
            routeTo: FreelancerSignupStepRoute.YOUR_TOP_SKILLS,
            group: FreelancerSignupStepGroup.SHOWCASE_YOUR_TALENT,
            isAccessible: _workflowSatisfies(workflow, {
                context: [FreelancerSignupContext.REGULAR, FreelancerSignupContext.OPEN],
                completed: [SignupCompletionField.Headline, SignupCompletionField.Categories],
            }),
            isCompleted: _workflowSatisfiesOneOf(workflow, [
                {
                    context: [FreelancerSignupContext.OPEN],
                    skipped: [SignupCompletionField.RegularSkills],
                },
                {
                    context: [FreelancerSignupContext.OPEN, FreelancerSignupContext.REGULAR],
                    completed: [SignupCompletionField.RegularSkills],
                },
            ]),
            previousEnabled: true,
            hideShapes: true,
        });
    }

    allStepDefinition.push(
        {
            name: FreelancerSignupStepName.WORKING_LANGUAGES,
            routeTo: FreelancerSignupStepRoute.WORKING_LANGUAGES,
            group: FreelancerSignupStepGroup.SHOWCASE_YOUR_TALENT,
            isAccessible: _workflowSatisfiesOneOf(workflow, [
                {
                    context: [FreelancerSignupContext.OPEN],
                    skipped: [SignupCompletionField.RegularSkills],
                },
                {
                    context: [FreelancerSignupContext.REGULAR, FreelancerSignupContext.OPEN],
                    completed: [SignupCompletionField.RegularSkills],
                },
                {
                    context: [FreelancerSignupContext.STRATEGY],
                    completed: [SignupCompletionField.IndustryExpertises],
                },
            ]),
            isCompleted: _workflowSatisfies(workflow, {
                completed: [SignupCompletionField.Languages],
            }),
            previousEnabled: true,
            hideShapes: true,
        },
        {
            name: FreelancerSignupStepName.LEVEL_OF_EXPERIENCE,
            routeTo: FreelancerSignupStepRoute.LEVEL_OF_EXPERIENCE,
            group: FreelancerSignupStepGroup.SHOWCASE_YOUR_TALENT,
            isAccessible: _workflowSatisfies(workflow, {
                completed: [SignupCompletionField.Languages],
            }),
            isCompleted: _workflowSatisfies(workflow, {
                completed: [SignupCompletionField.ExperienceLevel],
            }),
            previousEnabled: true,
            hideShapes: true,
        },
        {
            name: FreelancerSignupStepName.LOCATION_AND_WORKPLACE_PREFERENCE,
            routeTo: FreelancerSignupStepRoute.LOCATION_AND_WORKPLACE_PREFERENCE,
            group: FreelancerSignupStepGroup.SET_PREFERENCES,
            isAccessible: _workflowSatisfies(workflow, {
                completed: [SignupCompletionField.ExperienceLevel],
            }),
            isCompleted: _workflowSatisfies(workflow, {
                completed: [SignupCompletionField.LocationAndWorkplacePreference],
            }),
            previousEnabled: true,
            hideShapes: true,
        },
        {
            name: FreelancerSignupStepName.DAILY_RATE,
            routeTo: FreelancerSignupStepRoute.DAILY_RATE,
            group: FreelancerSignupStepGroup.SET_PREFERENCES,
            isAccessible: _workflowSatisfies(workflow, {
                completed: [SignupCompletionField.LocationAndWorkplacePreference],
            }),
            isCompleted: _workflowSatisfies(workflow, {
                completed: [SignupCompletionField.DailyRate],
            }),
            previousEnabled: true,
            hideShapes: true,
        },
    );
    if (addProfilePictureStep) {
        allStepDefinition.push({
            name: FreelancerSignupStepName.YOUR_PROFILE_PICTURE,
            routeTo: FreelancerSignupStepRoute.YOUR_PROFILE_PICTURE,
            group: FreelancerSignupStepGroup.READY_TO_GO,
            isAccessible: _workflowSatisfies(workflow, {
                completed: [SignupCompletionField.DailyRate],
            }),
            isCompleted: _workflowSatisfiesOneOf(workflow, [
                {
                    completed: [SignupCompletionField.ProfilePicture],
                },
                {
                    skipped: [SignupCompletionField.ProfilePicture],
                },
            ]),
            previousEnabled: true,
            hideShapes: true,
        });
    }

    if (_contextSatisfies(funnelContext, [FreelancerSignupContext.STRATEGY])) {
        allStepDefinition.push({
            name: FreelancerSignupStepName.SUBMIT_APPLICATION,
            routeTo: FreelancerSignupStepRoute.SUBMIT_APPLICATION,
            group: FreelancerSignupStepGroup.READY_TO_GO,
            isAccessible: _workflowSatisfiesOneOf(workflow, [
                {
                    completed: [SignupCompletionField.ProfilePicture],
                },
                {
                    skipped: [SignupCompletionField.ProfilePicture],
                },
            ]),
            isCompleted: _workflowSatisfies(workflow, {
                completed: [SignupCompletionField.PhoneNumber],
            }),
            previousEnabled: true,
            hideShapes: true,
        });
    } else {
        allStepDefinition.push(
            {
                name: FreelancerSignupStepName.YOUR_PHONE_NUMBER,
                routeTo: FreelancerSignupStepRoute.YOUR_PHONE_NUMBER,
                group: FreelancerSignupStepGroup.READY_TO_GO,
                isAccessible: _workflowSatisfiesOneOf(workflow, [
                    {
                        completed: [SignupCompletionField.ProfilePicture],
                    },
                    {
                        skipped: [SignupCompletionField.ProfilePicture],
                    },
                ]),
                isCompleted: _workflowSatisfiesOneOf(workflow, [
                    {
                        completed: [SignupCompletionField.PhoneNumber],
                    },
                    {
                        context: [FreelancerSignupContext.REGULAR, FreelancerSignupContext.OPEN],
                        skipped: [SignupCompletionField.PhoneNumber],
                    },
                ]),
                previousEnabled: true,
                hideShapes: true,
            },
            {
                name: FreelancerSignupStepName.YOUR_PRIVACY_SETTINGS,
                routeTo: FreelancerSignupStepRoute.YOUR_PRIVACY_SETTINGS,
                group: FreelancerSignupStepGroup.READY_TO_GO,
                isAccessible: _workflowSatisfiesOneOf(workflow, [
                    {
                        completed: [SignupCompletionField.PhoneNumber],
                    },
                    {
                        skipped: [SignupCompletionField.PhoneNumber],
                    },
                ]),
                isCompleted: _workflowSatisfies(workflow, {
                    completed: [SignupCompletionField.PrivacyOptOut],
                }),
                previousEnabled: true,
                hideShapes: false,
            },
        );
    }

    return _withSteps(allStepDefinition);
}

function _defineFirstStepFromContext(
    context: FreelancerSignupContext,
    workflow: Workflow | null,
): Omit<FreelancerSignupStepConfiguration, 'subStep'> {
    switch (context) {
        case FreelancerSignupContext.REGULAR:
            return {
                name: FreelancerSignupStepName.FREELANCING_EXPERIENCE,
                routeTo: FreelancerSignupStepRoute.FREELANCING_EXPERIENCE,
                group: FreelancerSignupStepGroup.INTRODUCE_YOURSELF,
                isAccessible: true,
                isCompleted: !!workflow,
                previousEnabled: false,
                hideShapes: true,
            };
        case FreelancerSignupContext.STRATEGY:
            return {
                name: FreelancerSignupStepName.WELCOME_TO_MALT_STRATEGY,
                routeTo: FreelancerSignupStepRoute.WELCOME_TO_MALT_STRATEGY,
                group: FreelancerSignupStepGroup.INTRODUCE_YOURSELF,
                isAccessible: true,
                isCompleted: !!workflow,
                previousEnabled: false,
                hideShapes: false,
            };
        case FreelancerSignupContext.OPEN:
            return {
                name: FreelancerSignupStepName.WELCOME_TO_MALT_OPEN,
                routeTo: FreelancerSignupStepRoute.WELCOME_TO_MALT_OPEN,
                group: FreelancerSignupStepGroup.INTRODUCE_YOURSELF,
                isAccessible: true,
                isCompleted: !!workflow,
                previousEnabled: false,
                hideShapes: true,
            };
    }
}

function _contextSatisfies(context: FreelancerSignupContext, expectedContext: FreelancerSignupContext[]): boolean {
    return expectedContext.includes(context);
}

function _workflowSatisfiesOneOf(workflow: Workflow | null, conditions: Condition[]): boolean {
    return conditions.some((condition) => _workflowSatisfies(workflow, condition));
}

function _workflowSatisfies(
    workflow: Workflow | null,
    {state = undefined, completed = [], toComplete = [], skipped = [], context = undefined}: Condition,
): boolean {
    return (
        !!workflow &&
        (state ? workflow.status === state : true) &&
        (toComplete ? toComplete.every((step) => workflow.completionState[step] === SignupCompletionCompletionEnum.ToComplete) : true) &&
        (completed ? completed.every((step) => workflow.completionState[step] === SignupCompletionCompletionEnum.Completed) : true) &&
        (skipped ? skipped.every((step) => workflow.completionState[step] === SignupCompletionCompletionEnum.Skipped) : true) &&
        (context ? _contextSatisfies(workflow.context, context) : true)
    );
}

function _withSteps(allStepDefinition: Omit<FreelancerSignupStepConfiguration, 'subStep'>[]): FreelancerSignupStepConfiguration[] {
    let introSubStepNumber = 1;
    let talentSubStepNumber = 1;
    let prefSubStepNumber = 1;
    let readySubStepNumber = 1;
    return allStepDefinition.map((stepDef) => {
        let subStepNumber;
        switch (stepDef.group) {
            case FreelancerSignupStepGroup.INTRODUCE_YOURSELF:
                subStepNumber = introSubStepNumber++;
                break;
            case FreelancerSignupStepGroup.SHOWCASE_YOUR_TALENT:
                subStepNumber = talentSubStepNumber++;
                break;
            case FreelancerSignupStepGroup.SET_PREFERENCES:
                subStepNumber = prefSubStepNumber++;
                break;
            case FreelancerSignupStepGroup.READY_TO_GO:
                subStepNumber = readySubStepNumber++;
                break;
        }
        return {
            name: stepDef.name,
            routeTo: stepDef.routeTo,
            group: stepDef.group,
            subStep: subStepNumber,
            previousEnabled: stepDef.previousEnabled,
            isAccessible: stepDef.isAccessible,
            isCompleted: stepDef.isCompleted,
            hideShapes: stepDef.hideShapes,
        };
    });
}
